// Task Result Card Component
import React from 'react';
import { Card, Text, Group, Tooltip } from '@mantine/core';
import { IconMapPin, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { TaskListItem } from '../../../../../../types';
import { useLocalizedDate } from '../../../../../../hooks/useLocalizedDate';
import styles from './TaskResultCard.module.css';

interface TaskResultCardProps {
  task: TaskListItem;
  onClick: () => void;
  isSelected?: boolean;
}

export const TaskResultCard: React.FC<TaskResultCardProps> = ({
  task,
  onClick,
  isSelected = false,
}) => {
  const { t } = useTranslation();
  const { formatDateCompact } = useLocalizedDate();

  const dueDate = task.dueDate
    ? formatDateCompact(task.dueDate)
    : t('common.noDate');
  const location = task.location || t('common.noLocation');

  return (
    <Card
      padding="sm"
      className={`${styles.taskCard} ${isSelected ? styles.selected : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className={styles.selectionIndicator}>
          <IconCheck size={16} />
        </div>
      )}

      {/* Task title */}
      <Tooltip label={task.title} openDelay={500} withArrow>
        <Text className={styles.taskTitle} fw={500} size="sm" lineClamp={1}>
          {task.title}
        </Text>
      </Tooltip>

      {/* Location and Due Date - Single Row */}
      <Group
        gap="xs"
        className={styles.infoRow}
        wrap="nowrap"
        justify="space-between"
      >
        <Group gap="xs" className={styles.locationGroup} wrap="nowrap">
          <IconMapPin size={14} className={styles.locationIcon} />
          <Text size="xs" c="dimmed" className={styles.locationText}>
            {location}
          </Text>
        </Group>

        <Text size="xs" c="dimmed" className={styles.dueDate}>
          {dueDate}
        </Text>
      </Group>
    </Card>
  );
};
